/**
 * Chip picker + Play/Clear buttons — DOM ports of the amgcommon_v2
 * ChipsSelection and PlayClearButton widgets.
 */
import type { GameConfig } from '../types/game';

const COIN_BASE = 'https://imagebank.b-cdn.net/AMG/Common/imgCoins';

export interface ControlsHandlers {
  onSelectChip: (index: number) => void;
  onClear: () => void;
  onConfirm: () => void;
}

export class TableControls {
  private readonly host: HTMLElement;
  private readonly chipBar: HTMLElement;
  private readonly pcGroup: HTMLElement;
  private readonly clearBtn: HTMLElement;
  private readonly playBtn: HTMLElement;
  private chipPicks: HTMLElement[] = [];
  private readonly handlers: ControlsHandlers;
  private currentSelectedIndex = 0;

  constructor(handlers: ControlsHandlers) {
    const host = document.getElementById('dom-hud');
    if (!host) throw new Error('TableControls: #dom-hud missing');
    this.host = host;
    this.handlers = handlers;

    this.chipBar = document.createElement('div');
    this.chipBar.className = 'chip-bar';
    this.host.appendChild(this.chipBar);

    this.pcGroup = document.createElement('div');
    this.pcGroup.className = 'pc-buttons';

    this.clearBtn = this.makePcButton('pc-btn-clear', 'CLEAR');
    this.playBtn = this.makePcButton('pc-btn-play', null);
    this.pcGroup.append(this.clearBtn, this.playBtn);
    this.host.appendChild(this.pcGroup);

    this.bindPressFeedback(this.clearBtn, () => handlers.onClear());
    this.bindPressFeedback(this.playBtn, () => handlers.onConfirm());
  }

  private makePcButton(modifierClass: string, label: string | null): HTMLElement {
    const el = document.createElement('div');
    el.className = `pc-btn ${modifierClass}`;
    if (label != null) {
      const span = document.createElement('span');
      span.className = 'pc-btn-label';
      span.textContent = label;
      el.appendChild(span);
    }
    return el;
  }

  private bindPressFeedback(el: HTMLElement, onTap: () => void): void {
    const down = (): void => {
      if (el.classList.contains('disabled')) return;
      el.classList.add('pressed');
    };
    const up = (): void => {
      if (!el.classList.contains('pressed')) return;
      el.classList.remove('pressed');
      if (!el.classList.contains('disabled')) onTap();
    };
    const cancel = (): void => el.classList.remove('pressed');
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointerleave', cancel);
    el.addEventListener('pointercancel', cancel);
  }

  bindChips(config: GameConfig): void {
    this.chipBar.innerHTML = '';
    this.chipPicks = [];
    config.chipValues.forEach((value, i) => {
      const pick = document.createElement('div');
      pick.className = 'chip-pick';
      const ring = document.createElement('div');
      ring.className = 'chip-ring';
      const coin = document.createElement('div');
      coin.className = 'chip-coin';
      coin.style.backgroundImage = `url('${COIN_BASE}${(i % 6) + 1}.png')`;
      const sweep = document.createElement('div');
      sweep.className = 'chip-sweep';
      coin.appendChild(sweep);
      const label = document.createElement('div');
      label.className = 'chip-label';
      label.textContent = formatChipLabel(value);
      coin.appendChild(label);

      pick.append(ring, coin);
      pick.addEventListener('click', () => this.handlers.onSelectChip(i));
      this.chipBar.appendChild(pick);
      this.chipPicks.push(pick);
    });
    this.setSelectedChip(this.currentSelectedIndex);
  }

  setSelectedChip(index: number): void {
    this.currentSelectedIndex = index;
    this.chipPicks.forEach((b, i) => b.classList.toggle('active', i === index));
  }

  setControlsEnabled(canBet: boolean, hasBets: boolean): void {
    this.chipBar.classList.toggle('disabled', !canBet);
    this.clearBtn.classList.toggle('disabled', !(canBet && hasBets));
    this.playBtn.classList.toggle('disabled', !(canBet && hasBets));
  }
}

function formatChipLabel(v: number): string {
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    return Number.isInteger(m) ? `${m}M` : `${m.toFixed(1)}M`;
  }
  if (v >= 1_000) {
    const k = v / 1_000;
    return Number.isInteger(k) ? `${k}K` : `${k.toFixed(1)}K`;
  }
  return String(v);
}
